/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Link2, RefreshCw } from 'lucide-react';
import type { ProjectRepoMappingRow } from '../api';
import {
  deleteProjectRepoMapping,
  startGithubRepoLinkOauth,
  syncProjectRepoMapping,
  updateProjectRepoMapping
} from '../api';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

export const REPO_LINK_PENDING_KEY = 'cindro:pendingRepoLink';

function ProjectPicker({
  selectedIds,
  onChange,
  disabled
}: {
  selectedIds: string[];
  onChange: (projectIds: string[]) => void;
  disabled?: boolean;
}) {
  const { workspace } = useWorkspace();
  const duplicateNames = new Set(
    workspace.apps
      .filter(
        (app, index, apps) =>
          apps.findIndex((candidate) => candidate.name === app.name) !== index
      )
      .map((app) => app.name)
  );
  return (
    <div className="repoProjectPicker" aria-label="Affected projects">
      {workspace.apps.map((app) => {
        const selected = selectedIds.includes(app.id);
        return (
          <label key={app.id} className={selected ? 'is-selected' : ''}>
            <input
              type="checkbox"
              checked={selected}
              disabled={disabled}
              onChange={() =>
                onChange(
                  selected
                    ? selectedIds.filter((id) => id !== app.id)
                    : [...selectedIds, app.id]
                )
              }
            />
            {app.name}
            {duplicateNames.has(app.name) ? ` · ${app.slug}` : ''}
          </label>
        );
      })}
    </div>
  );
}

function RepoMappingRow({
  mapping,
  syncingId,
  savingId,
  onSync,
  onDelete,
  onProjectsChange
}: {
  mapping: ProjectRepoMappingRow;
  syncingId: string;
  savingId: string;
  onSync: () => void;
  onDelete: () => void;
  onProjectsChange: (ids: string[]) => void;
}) {
  return (
    <div className="repoMappingRow">
      <div className="repoMappingIdentity">
        <Link2 size={15} />
        <div>
          <strong>{mapping.externalRepo}</strong>
          <span>GitHub signals · sync checks .cindro/pipeline.yml</span>
        </div>
      </div>
      <ProjectPicker
        selectedIds={mapping.projectIds}
        onChange={onProjectsChange}
        disabled={savingId === mapping.projectRepoMappingId}
      />
      <div className="repoMappingActions">
        <button
          type="button"
          className="secondaryButton"
          disabled={syncingId === mapping.projectRepoMappingId}
          onClick={onSync}
        >
          <RefreshCw size={14} />
          {syncingId === mapping.projectRepoMappingId
            ? 'Syncing…'
            : 'Sync repo'}
        </button>
        <button type="button" className="dangerButton" onClick={onDelete}>
          Remove
        </button>
      </div>
    </div>
  );
}

export function RepoMappingsCard() {
  const { session } = useSession();
  const { workspace, setWorkspaceVersion } = useWorkspace();
  const { toast, confirm } = useFeedback();
  const { projectRepoMappings } = workspace;
  const [externalRepo, setExternalRepo] = useState('');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [syncingId, setSyncingId] = useState('');
  const [savingId, setSavingId] = useState('');

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || busy) return;
    const repo = externalRepo.trim();
    if (!repo.includes('/')) {
      setError('Use owner/repo format, for example nerds-cooking/skillora.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      sessionStorage.setItem(
        REPO_LINK_PENDING_KEY,
        JSON.stringify({ externalRepo: repo, projectIds })
      );
      const { authorizeUrl } = await startGithubRepoLinkOauth(
        session.accessToken,
        repo
      );
      window.location.href = authorizeUrl;
    } catch (err: any) {
      sessionStorage.removeItem(REPO_LINK_PENDING_KEY);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to start GitHub authorization.'
      );
      setBusy(false);
    }
  }

  async function handleSync(mapping: ProjectRepoMappingRow) {
    if (!session || syncingId) return;
    setSyncingId(mapping.projectRepoMappingId);
    try {
      const result = await syncProjectRepoMapping(
        session.accessToken,
        mapping.projectRepoMappingId
      );
      setWorkspaceVersion((version) => version + 1);
      const definitionStatus = result.definition?.status || 'missing';
      toast(
        definitionStatus === 'invalid' ? 'error' : 'success',
        definitionStatus === 'activated'
          ? `Synced ${result.synced} signals and activated ${result.definition?.name}`
          : definitionStatus === 'invalid'
            ? `Signals synced, but .cindro/pipeline.yml is invalid${result.definition?.message ? `: ${result.definition.message}` : ''}`
            : `Synced ${result.synced} signals from ${result.repo}; no .cindro/pipeline.yml found`
      );
    } catch (err: any) {
      toast(
        'error',
        err?.response?.data?.message ||
          err?.message ||
          `Failed to sync ${mapping.externalRepo}.`
      );
    } finally {
      setSyncingId('');
    }
  }

  async function handleProjectsChange(
    mapping: ProjectRepoMappingRow,
    nextProjectIds: string[]
  ) {
    if (!session || savingId) return;
    setSavingId(mapping.projectRepoMappingId);
    try {
      await updateProjectRepoMapping(
        session.accessToken,
        mapping.projectRepoMappingId,
        nextProjectIds
      );
      setWorkspaceVersion((version) => version + 1);
    } catch (err: any) {
      toast(
        'error',
        err?.response?.data?.message ||
          err?.message ||
          'Failed to update affected projects.'
      );
    } finally {
      setSavingId('');
    }
  }

  async function handleDelete(mapping: ProjectRepoMappingRow) {
    if (!session) return;
    const confirmed = await confirm({
      title: 'Remove signal source?',
      message: `Cindro will stop recording new activity from "${mapping.externalRepo}". Existing journey history is retained.`,
      confirmLabel: 'Remove source',
      danger: true
    });
    if (!confirmed) return;
    try {
      await deleteProjectRepoMapping(
        session.accessToken,
        mapping.projectRepoMappingId
      );
      setWorkspaceVersion((version) => version + 1);
      toast('success', `Disconnected ${mapping.externalRepo}`);
    } catch (err: any) {
      toast(
        'error',
        err?.response?.data?.message ||
          err?.message ||
          'Failed to remove signal source.'
      );
    }
  }

  return (
    <SectionCard
      className="pipelineSetupPanel"
      label="Connections"
      title="Engineering signal sources"
      description="Connect each repository once, then mark every project it can affect. Sync imports evidence and activates .cindro/pipeline.yml when present; shared delivery repos can remain evidence-only."
      action={null}
    >
      <form onSubmit={handleCreate} className="repoConnectForm">
        <label className="settingsField">
          <span>GitHub repository</span>
          <input
            value={externalRepo}
            onChange={(event) => setExternalRepo(event.target.value)}
            placeholder="nerds-cooking/skillora-deploy"
          />
        </label>
        <div className="settingsField">
          <span>Affects these projects</span>
          <ProjectPicker selectedIds={projectIds} onChange={setProjectIds} />
        </div>
        <button
          type="submit"
          className="primaryButton"
          disabled={busy || !externalRepo.trim()}
        >
          {busy ? 'Connecting…' : 'Connect repo'}
        </button>
        {error ? <p className="fieldError">{error}</p> : null}
      </form>
      {projectRepoMappings.length > 0 ? (
        <div className="repoMappingList">
          {projectRepoMappings.map((mapping) => (
            <RepoMappingRow
              key={mapping.projectRepoMappingId}
              mapping={mapping}
              syncingId={syncingId}
              savingId={savingId}
              onSync={() => handleSync(mapping)}
              onDelete={() => handleDelete(mapping)}
              onProjectsChange={(ids) => handleProjectsChange(mapping, ids)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          compact
          icon={<Link2 size={20} />}
          title="No engineering signals connected"
          description="Connect the source and delivery repositories used by this workspace."
        />
      )}
    </SectionCard>
  );
}
